import React, { useState, useRef, useEffect } from "react"
import { Flex, Upload, Button, Checkbox, Progress } from "antd"
import { UploadOutlined, DeleteOutlined, FileOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import styles from "./LicensesSection.module.css"
import IconWithBg from "ui/IconWithBg"
import { validateFile as validateFileUtil } from "utils/file-helpers"
import { toaster } from "components/common/Toast"

// File upload configuration constants
const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "image/jpeg",
  "image/jpg"
]

const MAX_FILE_SIZE_MB = 5

/**
 * Validates file type and size using common utility
 * @param {File} file - The file to validate
 * @returns {{ isValid: boolean, error: string | null }}
 */
const validateFile = (file) => {
  const result = validateFileUtil(file, {
    acceptedTypes: ACCEPTED_FILE_TYPES,
    maxSizeMB: MAX_FILE_SIZE_MB
  })

  if (!result.isValid && result.error.includes("Invalid file type")) {
    return { isValid: false, error: "You can only upload DOC, DOCX, PDF, TXT, JPG files!" }
  }

  return result
}

export const LicensesSection = ({ isExpanded, onToggle, licenses, onLicensesChange }) => {
  const [t] = useTranslation()
  const [fileList, setFileList] = useState([])
  const uploadIntervalsRef = useRef({})

  const handleUploadChange = (info) => {
    const { fileList: newFileList } = info

    setFileList(newFileList)

    const completedFiles = newFileList.filter((file) => file.status === "done" || file.percent === 100)

    if (completedFiles.length > 0) {
      const newLicenses = completedFiles.map((file) => ({
        uid: file.uid,
        name: file.name,
        size: file.size,
        file: file.originFileObj || file,
        progress: 100
      }))

      const existingUids = licenses.map((l) => l.uid)
      const uniqueNewLicenses = newLicenses.filter((l) => !existingUids.includes(l.uid))

      if (uniqueNewLicenses.length > 0) {
        onLicensesChange([...licenses, ...uniqueNewLicenses])
      }

      completedFiles.forEach((file) => {
        if (uploadIntervalsRef.current[file.uid]) {
          clearInterval(uploadIntervalsRef.current[file.uid])
          delete uploadIntervalsRef.current[file.uid]
        }
      })
    }
  }

  const handleRemoveLicense = (uidOrId) => {
    if (uploadIntervalsRef.current[uidOrId]) {
      clearInterval(uploadIntervalsRef.current[uidOrId])
      delete uploadIntervalsRef.current[uidOrId]
    }

    const licenseToRemove = licenses.find((license) => license.uid === uidOrId || license.id === uidOrId)

    if (licenseToRemove && licenseToRemove.id) {
      const updatedLicenses = licenses.map((license) => (license.id === uidOrId ? { ...license, _destroy: true } : license))
      onLicensesChange(updatedLicenses)
    } else {
      const updatedLicenses = licenses.filter((license) => license.uid !== uidOrId)
      onLicensesChange(updatedLicenses)
    }

    const updatedFileList = fileList.filter((file) => file.uid !== uidOrId)
    setFileList(updatedFileList)
  }

  useEffect(() => {
    return () => {
      Object.values(uploadIntervalsRef.current).forEach((interval) => clearInterval(interval))
    }
  }, [])

  const handleBeforeUpload = (file) => {
    const { isValid, error } = validateFile(file)

    if (!isValid) {
      toaster.error(error)
      return Upload.LIST_IGNORE
    }

    return true
  }

  const uploadProps = {
    multiple: true,
    showUploadList: false,
    fileList: fileList,
    accept: ".doc,.docx,.pdf,.txt,.jpg,.jpeg",
    beforeUpload: handleBeforeUpload,
    customRequest: ({ file, onSuccess, onProgress }) => {
      const { isValid, error } = validateFile(file)

      if (!isValid) {
        console.error(error)
        return
      }

      let progress = 0
      const interval = setInterval(() => {
        progress += 10
        onProgress({ percent: progress })

        if (progress >= 100) {
          clearInterval(interval)
          if (uploadIntervalsRef.current[file.uid]) {
            delete uploadIntervalsRef.current[file.uid]
          }
          onSuccess(file)
        }
      }, 100)

      uploadIntervalsRef.current[file.uid] = interval
    },
    onChange: handleUploadChange
  }

  return (
    <div className={styles.licenseSection}>
      <Flex align="center" gap={12}>
        <Checkbox checked={isExpanded} onChange={onToggle} />
        <Text className={styles.sectionTitle}>{t("staff.add_staff_modal.licenses_section.title")}</Text>
      </Flex>
      {isExpanded && (
        <div className={styles.expandedContent}>
          <Text className={styles.subText}>{t("staff.add_staff_modal.licenses_section.description")}</Text>
          <div className={styles.contentWrapper}>
            <div className={styles.uploadWrapper}>
              <Upload {...uploadProps}>
                <div className={styles.uploadArea}>
                  <IconWithBg
                    className={styles.deleteIcon}
                    height={18}
                    width={18}
                    icon={<UploadOutlined style={{ color: colors.textSecondary }} />}
                  />
                  <div className={styles.uploadText}>
                    <Text className={styles.uploadLink}>{t("staff.add_staff_modal.licenses_section.upload")}</Text>
                    <Text className={styles.uploadHint}>{t("staff.add_staff_modal.licenses_section.upload_hint")}</Text>
                  </div>
                </div>
              </Upload>
            </div>
            {(fileList.length > 0 || licenses.some((l) => l.id && !l._destroy)) && (
              <div className={styles.filesList}>
                {licenses
                  ?.filter((license) => license.id && !license._destroy && license.fileUrl)
                  ?.map((license) => (
                    <div key={license.id} className={styles.fileItem}>
                      <Flex align="center" gap={12} style={{ flex: 1 }}>
                        <FileOutlined style={{ fontSize: 20, color: colors.textSecondary }} />
                        <div className={styles.fileInfo}>
                          <Text className={styles.fileName}>{license.name}</Text>
                        </div>
                      </Flex>
                      <Flex align="center" gap={8}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveLicense(license.id)}
                          className={styles.deleteButton}
                        />
                      </Flex>
                    </div>
                  ))}
                {fileList?.map((file) => {
                  const isUploading = file.status === "uploading" || (file.percent && file.percent < 100)
                  return (
                    <div key={file.uid} className={styles.fileItem}>
                      <Flex align="center" gap={12} style={{ flex: 1 }}>
                        <FileOutlined style={{ fontSize: 20, color: colors.textSecondary }} />
                        <div className={styles.fileInfo}>
                          <Text className={styles.fileName}>{file.name}</Text>
                          {isUploading && <Progress size={"small"} className={styles.progressBar} percent={file.percent || 0} />}
                        </div>
                      </Flex>
                      <Flex align="center" gap={8}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveLicense(file.uid)}
                          className={styles.deleteButton}
                        />
                      </Flex>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
